import { useMemo, useState } from 'react'
import { PackageSearch } from 'lucide-react'
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  MoneyValue,
  Skeleton,
  StatusBadge,
  TextField,
} from '@shared/design-system/ui'
import { useSession } from '@shared/lib/auth/session-store'
import { hasAdminPermission } from '@shared/lib/auth/permissions'
import { useAdminProduct, useAdminProducts } from '../api/admin.api'
import type { ProductItem } from '../api/admin.api'
import { TechnicalId } from './TechnicalId'
import { AdminUserSearchField } from './AdminUserSearchField'
import { useResolvedAdminUser } from './useResolvedAdminUser'
import {
  compactCell,
  filters,
  header,
  page,
  subtitle,
  table,
  tableCard,
  tableViewport,
  td,
  th,
  title,
  titleBlock,
  value,
} from './admin.css'

type AdminProductsScreenProps = {
  onEditProduct: (product: ProductItem) => void
}

export function AdminProductsScreen({ onEditProduct }: AdminProductsScreenProps) {
  const session = useSession((state) => state.user)
  const canReadUsers = hasAdminPermission(session, 'USER_READ')
  const canWrite = hasAdminPermission(session, 'PRODUCT_WRITE')
  const [userQuery, setUserQuery] = useState('')
  const [directProductId, setDirectProductId] = useState('')
  const [requestedProductId, setRequestedProductId] = useState('')
  const normalizedDirectProductId = directProductId.trim()
  const userFilter = useResolvedAdminUser(userQuery)
  const resolvedUserId = canReadUsers ? userFilter.resolvedUserId : ''
  const products = useAdminProducts(resolvedUserId)
  const directProduct = useAdminProduct(requestedProductId)
  const listItems = useMemo(() => products.data?.items ?? [], [products.data?.items])

  return (
    <section className={page} data-testid="admin-products-screen">
      <div className={header}>
        <div className={titleBlock}>
          <h1 className={title}>Produtos</h1>
          <p className={subtitle}>
            Acesso de contingência para perfis de produto. Consulte pelo ID ou, com permissão de
            usuários, localize o catálogo pelo produtor.
          </p>
        </div>
      </div>

      <Card padding="md" data-testid="admin-products-filters">
        <div className={filters}>
          {canReadUsers ? (
            <AdminUserSearchField value={userQuery} onChange={setUserQuery} state={userFilter} />
          ) : null}
          <TextField
            label="Produto"
            value={directProductId}
            onChange={(event) => {
              setDirectProductId(event.currentTarget.value)
              setRequestedProductId('')
            }}
            placeholder="productId direto"
          />
          <Button
            variant="secondary"
            leftIcon={<PackageSearch size={16} />}
            disabled={!normalizedDirectProductId}
            data-testid="admin-products-lookup-btn"
            onClick={() => setRequestedProductId(normalizedDirectProductId)}
          >
            Consultar produto
          </Button>
        </div>
      </Card>

      {requestedProductId && directProduct.isPending ? (
        <Card padding="md">
          <Skeleton height={180} />
        </Card>
      ) : requestedProductId && directProduct.isError ? (
        <Card padding="md">
          <ErrorState onRetry={() => void directProduct.refetch()} />
        </Card>
      ) : directProduct.data?.product ? (
        <ProductsTable
          items={[directProduct.data.product]}
          canWrite={canWrite}
          onEditProduct={onEditProduct}
        />
      ) : null}

      {canReadUsers && resolvedUserId && products.isPending ? (
        <Card padding="md">
          <Skeleton height={240} />
        </Card>
      ) : canReadUsers && resolvedUserId && products.isError ? (
        <Card padding="md">
          <ErrorState onRetry={() => void products.refetch()} />
        </Card>
      ) : canReadUsers && listItems.length > 0 ? (
        <ProductsTable items={listItems} canWrite={canWrite} onEditProduct={onEditProduct} />
      ) : canReadUsers ? (
        <Card padding="md">
          <EmptyState
            icon={<PackageSearch size={24} />}
            title={
              userFilter.notFound
                ? 'Usuário não encontrado'
                : resolvedUserId && !products.isLoading
                  ? 'Nenhum produto encontrado'
                  : 'Busque um produtor'
            }
            description={
              userFilter.notFound
                ? 'Confira o e-mail informado para consultar os produtos desse produtor.'
                : resolvedUserId && !products.isLoading
                  ? 'Quando esse produtor tiver produtos cadastrados, eles aparecerão aqui.'
                  : 'Informe o e-mail do produtor para listar produtos ou cole um productId.'
            }
          />
        </Card>
      ) : null}
    </section>
  )
}

function ProductsTable({
  items,
  canWrite,
  onEditProduct,
}: {
  items: ProductItem[]
  canWrite: boolean
  onEditProduct: (product: ProductItem) => void
}) {
  return (
    <Card padding="none" className={tableCard}>
      <div className={tableViewport}>
        <table className={table} data-testid="admin-products-table">
          <thead>
            <tr>
              <th className={th}>Produto</th>
              <th className={th}>Preço</th>
              <th className={th}>Status</th>
              <th className={th}>Checkouts</th>
              <th className={th}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} data-testid={`admin-product-row-${item.id}`}>
                <td className={`${td} ${compactCell}`}>
                  <span className={value}>{item.name}</span>
                  <br />
                  <TechnicalId label="ID" value={item.id} />
                </td>
                <td className={td}>
                  <MoneyValue amount={item.price.amount} currency={item.price.currency} />
                </td>
                <td className={td}>
                  <StatusBadge
                    label={item.status === 'ACTIVE' ? 'Ativo' : item.status}
                    tone={item.status === 'ACTIVE' ? 'success' : 'neutral'}
                  />
                </td>
                <td className={td}>{item.checkouts.length}</td>
                <td className={td}>
                  {canWrite ? (
                    <Button size="sm" variant="secondary" onClick={() => onEditProduct(item)}>
                      Solicitar alteração
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
